import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../cn.js';
import { Spinner } from './Spinner.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'xs' | 'sm' | 'md';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-on-brand hover:bg-brand-hover border border-transparent',
  secondary: 'bg-surface text-body border border-line-strong hover:bg-raised',
  ghost: 'bg-transparent text-muted border border-transparent hover:bg-raised hover:text-body',
  danger: 'bg-danger-bg text-danger-fg border border-danger-border hover:opacity-90',
  link: 'bg-transparent text-brand-text border border-transparent hover:underline p-0',
};

const SIZES: Record<ButtonSize, string> = {
  xs: 'px-2.5 py-1.5 text-xs gap-1.5',
  sm: 'px-3 py-1.5 text-sm gap-2',
  md: 'px-4 py-2 text-sm gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  /**
   * Renders as a square icon button: it changes the padding, nothing else.
   * `aria-label` becomes mandatory, because a glyph has no accessible name.
   *
   * It deliberately does not suppress `children`. It used to, and the result
   * was four invisible destructive buttons -- a trash icon passed as a child
   * rather than as `leadingIcon` produced a transparent, clickable, empty
   * box. A prop that silently discards what it was given is a trap, and the
   * only feedback it gives is a control nobody can find.
   */
  iconOnly?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'sm',
    loading = false,
    iconOnly = false,
    leadingIcon,
    trailingIcon,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      // Defaulting to "button" rather than the HTML default of "submit" stops
      // a decorative button inside a form from submitting it by accident.
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-medium rounded-lg transition-colors whitespace-nowrap',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
        VARIANTS[variant],
        iconOnly ? (size === 'xs' ? 'p-1.5' : 'p-2') : SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner className="w-4 h-4" /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  );
});
